/**
 * CRUD thunk factory for the warehouse api.js files.
 *
 * orders/api.js and products/api.js each define, once per entity, the same
 * createAsyncThunk shape: fetchMany unwraps `.results`, fetchOne/create/patch
 * optionally normalize the response, remove returns the deleted id, and every
 * case reports failure via `rejectWithValue(e.response.data)`. This factory
 * dedupes that boilerplate. It is warehouse-only and contained on purpose —
 * the createAsyncThunk+normalizr lineage it serves is already sunset
 * elsewhere in favor of RTK Query (see utils/slice.js's createEntitySlice
 * header); this factory is not meant to spread to billing, customers, or new
 * resources.
 *
 * Contains:
 * - createCrudThunks — builds the subset of thunks named in `handlers`
 */
import { createAsyncThunk } from '@reduxjs/toolkit';

const withErrorHandling =
  fn =>
  async (arg, { rejectWithValue }) => {
    try {
      return await fn(arg);
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  };

/**
 * @param {string} actionPrefix - e.g. `'warehouse/vendors'`; reproduces the
 *   pre-extraction `${actionPrefix}/fetchMany` etc. action-type strings.
 * @param {Object} handlers - transport fns to wrap; a thunk is generated only
 *   for the keys present: fetchMany(queryParams), fetchOne(id), create(payload),
 *   patch(id, payload), remove(id).
 * @param {Function} [transform] - applied to fetchOne/create/patch responses
 *   (e.g. `(data) => normalize(data, XSchema)`); defaults to identity, which
 *   matches the entities that return the raw response. fetchMany always
 *   returns `.results` untransformed, matching every existing entity.
 * @returns {Object} thunks keyed `loadMany`/`loadOne`/`create`/`edit`/`remove`
 */
export const createCrudThunks = (actionPrefix, handlers, transform = data => data) => {
  const thunks = {};

  if (handlers.fetchMany) {
    thunks.loadMany = createAsyncThunk(
      `${actionPrefix}/fetchMany`,
      withErrorHandling(async queryParams => (await handlers.fetchMany(queryParams)).results),
    );
  }
  if (handlers.fetchOne) {
    thunks.loadOne = createAsyncThunk(
      `${actionPrefix}/fetchOne`,
      withErrorHandling(async id => transform(await handlers.fetchOne(id))),
    );
  }
  if (handlers.create) {
    thunks.create = createAsyncThunk(
      `${actionPrefix}/create`,
      withErrorHandling(async payload => transform(await handlers.create(payload))),
    );
  }
  if (handlers.patch) {
    thunks.edit = createAsyncThunk(
      `${actionPrefix}/patch`,
      withErrorHandling(async ({ id, ...payload }) => transform(await handlers.patch(id, payload))),
    );
  }
  if (handlers.remove) {
    thunks.remove = createAsyncThunk(
      `${actionPrefix}/delete`,
      withErrorHandling(async id => {
        await handlers.remove(id);
        return id;
      }),
    );
  }

  return thunks;
};
